import React from 'react';
import { useSelector } from 'react-redux';

import {
  Dropdown,
  Input,
  Label,
  RadioGroup,
  TextArea
} from '@components/index';
import SelectDateInput from '@features/member-management/components/SelectDateInput';
import {
  bloodGroupOptions,
  isInuredOptions
} from '@features/member-management/constants';
import { UserProfileCustomField } from '@features/member-management/types';
import { AddMemberFormDataType, ValidationError } from '@types';
import { snakeToCamel } from '@utils/stringFormat';

interface MemberCustomFieldsProps {
  formData: AddMemberFormDataType;
  handleFormDataChange: (
    field: keyof AddMemberFormDataType,
    value: string
  ) => void;
  error: ValidationError;
  containerRef: React.RefObject<HTMLElement>;
  onFocused: () => void;
}

const FieldLabel = ({ field }: { field: UserProfileCustomField }) => (
  <Label label={field.label} truncate tooltipId={field.key} />
);

const MemberCustomFields = ({
  formData,
  handleFormDataChange,
  error,
  containerRef,
  onFocused
}: MemberCustomFieldsProps) => {
  const { memberCustomFields } = useSelector(
    (state: any) => state.branchConfig
  );

  const customFields: UserProfileCustomField[] = memberCustomFields || [];

  if (customFields.length === 0) return null;

  const renderField = (field: UserProfileCustomField) => {
    const formKey = snakeToCamel(field.key) as keyof AddMemberFormDataType;
    const value = (formData[formKey] as string) || '';

    const onChange = (val: string) => {
      handleFormDataChange(formKey, val);
    };

    if (field.key === 'blood_group' && field.type === 'varchar') {
      return (
        <div key={field.key}>
          <FieldLabel field={field} />
          <Dropdown
            placeholder="Select Blood Group"
            className="h-12.5 w-full rounded-lg !bg-cofeeNeutral1"
            currentFilter={value}
            optionsStyle="!max-h-56 overflow-y-auto"
            onSelectFilter={onChange}
            filterOptions={bloodGroupOptions}
            usePortal
            closeOnOutsideScroll
          />
        </div>
      );
    }

    switch (field.type) {
      case 'varchar':
        return (
          <div key={field.key}>
            <FieldLabel field={field} />
            <Input
              value={value}
              handleInputChange={onChange}
              type="text"
              placeholder={`Enter ${field.label?.toLowerCase() || ''}`}
              maxLength={100}
              errorMessage={error[formKey]}
            />
          </div>
        );

      case 'number':
        return (
          <div key={field.key}>
            <FieldLabel field={field} />
            <Input
              value={value}
              handleInputChange={onChange}
              type="number"
              placeholder={`Enter ${field.label?.toLowerCase() || ''}`}
              errorMessage={error[formKey]}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.key} className="col-span-2">
            <FieldLabel field={field} />
            <TextArea
              value={value}
              handleChange={(e) => onChange(e.target.value)}
              placeholder={`Enter ${field.label?.toLowerCase() || ''}`}
              maxLength={500}
            />
          </div>
        );

      case 'date':
        return (
          <SelectDateInput
            key={field.key}
            modalTitle={`Select ${field.label}`}
            label={field.label}
            truncateLabel
            labelTooltipId={field.key}
            datePickerLabel="__/__/__"
            handleDateSelect={(date?: Date) => {
              onChange(date?.toLocaleDateString('en-CA') ?? '');
            }}
            selectedDate={value}
            onClearEndDate={() => onChange('')}
          />
        );

      case 'dropdown': {
        const dropdownOptions = (field.options || []).map((option) => ({
          id: option,
          value: option,
          label: option
        }));
        return (
          <div key={field.key}>
            <FieldLabel field={field} />
            <Dropdown
              placeholder={`Select ${field.label?.toLowerCase() || ''}`}
              className="h-12.5 w-full rounded-lg !bg-cofeeNeutral1"
              currentFilter={value}
              optionsStyle="!max-h-56 overflow-y-auto"
              onSelectFilter={onChange}
              filterOptions={dropdownOptions}
              usePortal
              closeOnOutsideScroll
            />
          </div>
        );
      }

      case 'bool':
        return (
          <div key={field.key}>
            <FieldLabel field={field} />
            <RadioGroup
              selectedValue={value}
              options={isInuredOptions}
              handleChange={onChange}
              className="h-12.5 items-center"
            />
          </div>
        );

      default:
        return (
          <div key={field.key}>
            <FieldLabel field={field} />
            <Input
              value={value}
              handleInputChange={onChange}
              type="text"
              placeholder={`Enter ${field.label?.toLowerCase() || ''}`}
              maxLength={100}
            />
          </div>
        );
    }
  };

  return (
    <div
      role="presentation"
      ref={containerRef as React.RefObject<HTMLDivElement>}
      onKeyDown={onFocused}
      onClick={onFocused}
      onFocus={onFocused}
      className="grid w-full grid-cols-2 gap-4 rounded-xl bg-theme p-6 shadow-tableShadow"
    >
      {customFields.map(renderField)}
    </div>
  );
};

export default MemberCustomFields;
